import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X } from 'lucide-react';

// Même valeur sentinelle que `CreatableSelectField` : ce champ REPREND l'UX que
// le formulaire « Nouveau Projet » applique déjà au même champ — une liste
// déroulante dont la dernière entrée bascule en saisie. Deux contrôles côte à
// côte (liste + champ de création toujours visible) racontaient deux histoires
// pour une seule décision.
const CREATE_NEW_ACCOUNT = '__create_new_account__';
import { TaskTypeConfigSection, buildTaskTypesMap, RightsMatrix, Picto, isPictoName } from '../shared';
import { fetchProjectRights, saveProjectRights } from '../../services/projectRights';
import { fetchProjectRefKey, saveProjectRefKey, fetchAgentKey, saveAgentKey } from '../../services/projectSettings';
import WorkflowViewModal from '../workflow/WorkflowViewModal';
import GroupMembersManager from '../shared/ui/GroupMembersManager';
import { IconLibrary } from '../builder/editors/IconLibrary';

// CDC-TMA T16: le picto du projet est une ICÔNE SVG (Lucide) désignée par son
// nom, choisie dans la bibliothèque d'icônes — la même pop-in que le builder.
// La grille de vingt emoji qu'elle remplace ne couvrait aucun métier et se
// rendait différemment d'un système à l'autre.

/**
 * Project configuration modal.
 *
 * Props:
 *   isOpen          boolean
 *   onClose         () => void
 *   title           string
 *   settings        object  — current project settings
 *   onSave          (newSettings) => void
 *   users           array   — available users
 *   taskTypes       array   — raw backend task-type items (same shape as backendTaskTypes
 *                             in ConfigurationPage), populated by KanbanHeader from YAML
 *   canEditWorkflow boolean — whether the current user can open the full workflow editor
 *   methodology / account       current values (ids as strings, '' when unset)
 *   methodologyOptions / accountOptions  [{ value, label }]
 *   onAccountCreated ({ value, label }) => void — OPTIONNEL mais vivement
 *                             conseillé : sans lui, la liste de clients de la
 *                             page reste celle du chargement, et le client
 *                             fraîchement créé disparaît du <select> à la
 *                             réouverture (il est pourtant bien enregistré).
 *   hosts           array   — domaines revendiqués par le projet, en items
 *                             `{ value }` : c'est la forme que le back rend ET
 *                             attend, et celle qui repart telle quelle.
 *   agentInstance   string  — adresse de l'instance qui exécute l'agent IA des
 *                             tickets du projet ('' = aucune). Sa clé d'API se
 *                             saisit dans la même section, mais sur sa propre
 *                             route (entité Key) : elle ne passe pas par onSave.
 */
const ProjectSettingsModal = ({
  isOpen,
  onClose,
  title,
  description = '',
  picto = '',
  methodology = '',
  account = '',
  methodologyOptions = [],
  accountOptions = [],
  hosts = [],
  agentInstance = '',
  onAccountCreated,
  settings = {},
  onSave,
  users = [],
  taskTypes = [],
  canEditWorkflow = false,
  projectId = null,
  onMembersChanged,
}) => {
  const [activeTab, setActiveTab] = useState('general');

  const [projectTitle, setProjectTitle] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectPicto, setProjectPicto] = useState('');
  const [pictoLibraryOpen, setPictoLibraryOpen] = useState(false);
  const [projectMethodology, setProjectMethodology] = useState('');
  const [projectAccount, setProjectAccount] = useState('');
  // Clients créés à la volée depuis cette pop-in : le magasin de la page n'est
  // pas rafraîchi tant qu'elle est ouverte, la nouvelle option vit donc ici.
  const [createdAccounts, setCreatedAccounts] = useState([]);
  const [accountCreateMode, setAccountCreateMode] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [accountCreating, setAccountCreating] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [projectHosts, setProjectHosts] = useState([]);
  const [newHost, setNewHost] = useState('');
  const [projectAgentInstance, setProjectAgentInstance] = useState('');
  // Clé d'API de l'instance : seul son ÉTAT est lu (enregistrée ou non, préfixe
  // public) ; la saisie est en écriture seule et ne part qu'à l'enregistrement.
  const [agentKey, setAgentKey] = useState({ set: false, prefix: '' });
  const [agentKeyInput, setAgentKeyInput] = useState('');
  const [agentKeyRemove, setAgentKeyRemove] = useState(false);
  const [agentKeyError, setAgentKeyError] = useState('');
  const [enabledTaskTypes, setEnabledTaskTypes] = useState({});
  const [workingHours, setWorkingHours] = useState({});

  // Rights matrix: owned by Drupal (group permissions), never by the project
  // settings JSON. { roles, types, general, tasks, customised } or null.
  const [rights, setRights] = useState(null);
  const [rightsError, setRightsError] = useState('');

  // Clé des tickets (« ORB » dans ORB-14) : servie et enregistrée par le module
  // orbit_ref, sur son propre endpoint (cf. projectSettings.js). null = champ
  // masqué — module désinstallé ou désactivé.
  const [refKeyInfo, setRefKeyInfo] = useState(null);
  const [refKey, setRefKey] = useState('');
  const [refKeyError, setRefKeyError] = useState('');
  const refKeyInputRef = useRef(null);

  const [viewWorkflowModal, setViewWorkflowModal] = useState({ isOpen: false, workflowId: null, workflowLabel: null });

  // Dédoublonnage par identifiant : un client créé ici est à la fois dans l'état
  // local (pour le cas où l'écran appelant ne fournit pas `onAccountCreated`) et
  // dans la liste du magasin, que ce même callback vient de mettre à jour.
  const allAccountOptions = [...accountOptions, ...createdAccounts].filter(
    (option, index, list) => list.findIndex((o) => String(o.value) === String(option.value)) === index,
  );

  const { map: taskTypesMap } = buildTaskTypesMap(taskTypes?.length ? taskTypes : null);
  const typeNames = Object.keys(taskTypesMap);

  useEffect(() => {
    if (!isOpen) return;
    setProjectTitle(title || '');
    setProjectDescription(description || '');
    setProjectPicto(picto || '');
    setProjectMethodology(methodology ? String(methodology) : '');
    setProjectAccount(account ? String(account) : '');
    setProjectHosts(Array.isArray(hosts) ? hosts : []);
    setNewHost('');
    setProjectAgentInstance(agentInstance || '');
    setNewAccountName('');
    setAccountCreateMode(false);
    setAccountError('');

    setEnabledTaskTypes(
      settings.enabledTaskTypes ||
      typeNames.reduce((acc, t) => ({ ...acc, [t]: true }), {})
    );

    setWorkingHours(settings.workingHours || {
      startMorning: '09:00',
      endMorning: '12:00',
      startAfternoon: '14:00',
      endAfternoon: '18:00',
    });
  }, [isOpen, settings]);

  // The matrix is read from Drupal, not from the project settings: it IS the
  // group permissions of this project.
  useEffect(() => {
    if (!isOpen || !projectId) {
      setRights(null);
      return undefined;
    }
    let cancelled = false;
    setRights(null);
    setRightsError('');
    fetchProjectRights(projectId)
      .then((data) => { if (!cancelled) setRights(data); })
      .catch((status) => {
        if (cancelled) return;
        setRightsError(status === 403
          ? "Vous n'avez pas les droits pour gérer la matrice de ce projet."
          : "La matrice de droits n'a pas pu être chargée.");
      });
    return () => { cancelled = true; };
  }, [isOpen, projectId]);

  useEffect(() => {
    if (!isOpen || !projectId) {
      setRefKeyInfo(null);
      return undefined;
    }
    let cancelled = false;
    setRefKeyInfo(null);
    setRefKey('');
    setRefKeyError('');
    fetchProjectRefKey(projectId)
      .then((info) => {
        if (cancelled || !info?.enabled) return;
        setRefKeyInfo(info);
        setRefKey(info.prefix || '');
      })
      // 404 (module absent) ou refus : le champ n'apparaît pas, rien d'autre.
      .catch(() => { if (!cancelled) setRefKeyInfo(null); });
    return () => { cancelled = true; };
  }, [isOpen, projectId]);

  // Only the task types enabled on this project carry a rights row. An empty
  // selection means "not configured yet" — show every type rather than nothing.
  const enabledTypeNames = Object.entries(enabledTaskTypes)
    .filter(([, on]) => on)
    .map(([name]) => name);
  const visibleTypes = (rights?.types || []).filter(
    (type) => enabledTypeNames.length === 0 || enabledTypeNames.includes(type.label)
  );

  /**
   * Persists the matrix. Returns false when the save was refused, so the modal
   * stays open on the tab instead of silently dropping the change.
   */
  const saveRights = async () => {
    if (!rights || !projectId) return true;
    try {
      setRights(await saveProjectRights(projectId, rights));
      return true;
    } catch (status) {
      setRightsError(status === 403
        ? "Vous n'avez pas les droits pour modifier la matrice de ce projet."
        : "La matrice de droits n'a pas pu être enregistrée.");
      return false;
    }
  };

  /**
   * Persists the ticket key when it changed. Returns false when it was refused
   * (key taken or malformed, no right), so the modal stays open on the General
   * tab with the reason.
   */
  const saveRefKey = async () => {
    if (!refKeyInfo || !refKeyInfo.canEdit || !projectId) return true;
    const next = refKey.trim().toUpperCase();
    if (next === (refKeyInfo.prefix || '')) return true;
    try {
      const info = await saveProjectRefKey(projectId, next);
      setRefKeyInfo(info);
      setRefKey(info.prefix || '');
      setRefKeyError('');
      return true;
    } catch (error) {
      setRefKeyError(error?.status === 403
        ? "Vous n'avez pas les droits pour changer la clé de ce projet."
        : (error?.message || "La clé n'a pas pu être enregistrée."));
      return false;
    }
  };

  // État de la clé d'API de l'instance de l'agent IA, relu à chaque ouverture.
  useEffect(() => {
    if (!isOpen || !projectId) return undefined;
    let cancelled = false;
    setAgentKey({ set: false, prefix: '' });
    setAgentKeyInput('');
    setAgentKeyRemove(false);
    setAgentKeyError('');
    fetchAgentKey(projectId)
      .then((data) => { if (!cancelled) setAgentKey({ set: !!data?.set, prefix: data?.prefix || '' }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, projectId]);

  /**
   * Enregistre ou retire la clé, sur sa propre route. Même contrat que
   * saveRights : false = refusé, la pop-in reste ouverte sur le message.
   */
  const saveAgentKeyIfNeeded = async () => {
    const key = agentKeyInput.trim();
    if (!projectId || (!key && !agentKeyRemove)) return true;
    try {
      const state = await saveAgentKey(projectId, key ? { key } : { remove: true });
      setAgentKey({ set: !!state?.set, prefix: state?.prefix || '' });
      setAgentKeyInput('');
      setAgentKeyRemove(false);
      return true;
    } catch (error) {
      setAgentKeyError(error?.status === 403
        ? "Vous n'avez pas les droits pour modifier la clé de ce projet."
        : (error?.error || "La clé n'a pas pu être enregistrée."));
      return false;
    }
  };

  // Création d'un client à la volée — le bundle `account` n'a pas d'écran
  // d'administration : sans ça, le champ resterait vide et inutilisable tant que
  // personne n'a créé de client depuis le formulaire « Nouveau Projet ».
  // Contrat Syncable Object : l'id créé est à la RACINE de la réponse.
  const handleCreateAccount = useCallback(async () => {
    const name = newAccountName.trim();
    if (!name || accountCreating) return;
    setAccountCreating(true);
    setAccountError('');
    const url = '/decoupled-api/save?display=decoupled&entity_type=node&bundle=account';
    try {
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submission_url: url, data: { title: name }, status: null }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const body = await res.json();
      const newId = String(body?.data?.id ?? body?.id ?? '');
      if (!newId) {
        setAccountError('Client créé, mais la réponse ne porte pas son identifiant.');
        return;
      }
      const option = { value: newId, label: name };
      setCreatedAccounts((prev) => [...prev, option]);
      setProjectAccount(newId);
      setNewAccountName('');
      setAccountCreateMode(false);
      if (onAccountCreated) onAccountCreated(option);
    } catch (e) {
      setAccountError(`Le client n'a pas pu être créé (${e.message}).`);
    } finally {
      setAccountCreating(false);
    }
  }, [newAccountName, accountCreating, onAccountCreated]);

  const cancelAccountCreate = useCallback(() => {
    setAccountCreateMode(false);
    setNewAccountName('');
    setAccountError('');
  }, []);

  const addHost = useCallback(() => {
    const value = newHost.trim().toLowerCase();
    if (!value) return;
    setProjectHosts((prev) => (
      prev.some((h) => (h?.value ?? h) === value) ? prev : [...prev, { value }]
    ));
    setNewHost('');
  }, [newHost]);

  const handleWorkflowClick = (typeName, meta) => {
    if (meta?.workflowId) {
      setViewWorkflowModal({ isOpen: true, workflowId: meta.workflowId, workflowLabel: typeName });
    }
    // When no workflow exists, the button is visible but does nothing — workflow
    // must be created from the global configuration page.
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-2xl w-[900px] max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <h2 className="text-xl font-bold text-slate-800">Configuration du projet</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          {[
            { id: 'types', label: 'Types de tâches' },
            { id: 'rights', label: 'Matrice de droits' },
            { id: 'general', label: 'Général' },
            { id: 'users', label: 'Utilisateurs' },
          ].map(tab => (
            <button
              key={tab.id}
              className={`px-6 py-3 font-medium text-sm focus:outline-none ${
                activeTab === tab.id
                  ? 'text-iris-700 border-b-2 border-iris-700 bg-iris-50'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-grow overflow-y-auto p-6 bg-slate-50">

          {/* Tab: Task Types */}
          {activeTab === 'types' && (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                <h3 className="text-lg font-medium text-slate-900 mb-4">Types de tâches activés</h3>
                <TaskTypeConfigSection
                  taskTypes={taskTypes}
                  enabledTypes={enabledTaskTypes}
                  onChange={(typeName) => setEnabledTaskTypes(prev => ({ ...prev, [typeName]: !prev[typeName] }))}
                  onWorkflowClick={handleWorkflowClick}
                />
              </div>
            </div>
          )}

          {/* Tab: Rights Matrix — served and enforced by Drupal group permissions. */}
          {activeTab === 'rights' && (
            <RightsMatrix
              rights={rights}
              onChange={setRights}
              visibleTypes={visibleTypes}
              error={rightsError}
            />
          )}

          {/* Tab: Users */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                <h3 className="text-lg font-medium text-slate-900 mb-4">Gestion des utilisateurs</h3>
                <GroupMembersManager
                  groupId={projectId}
                  availableUsers={users}
                  onMembersChanged={onMembersChanged}
                />
              </div>
            </div>
          )}

          {/* Tab: General & Hours */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              {/* CDC-TMA T14: project title (Group label) is editable here.
                  Server enforces "edit group" — a client PATCH returns 403. */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                <h3 className="text-lg font-medium text-slate-900 mb-4">Identité du projet</h3>
                <label className="field-label mb-2">Titre du projet</label>
                <input
                  type="text"
                  value={projectTitle}
                  onChange={(e) => setProjectTitle(e.target.value)}
                  placeholder="Nom du projet"
                  className="input"
                />
                {refKeyInfo && (
                  <>
                    <label className="field-label mb-2 mt-4" htmlFor="project-ref-key">Clé des tickets</label>
                    <div className="flex items-center gap-2">
                      <input
                        ref={refKeyInputRef}
                        type="text"
                        id="project-ref-key"
                        value={refKey}
                        onChange={(e) => { setRefKey(e.target.value.toUpperCase()); setRefKeyError(''); }}
                        readOnly={!refKeyInfo.canEdit}
                        maxLength={10}
                        autoComplete="off"
                        spellCheck={false}
                        placeholder={refKeyInfo.suggestion || 'ABC'}
                        aria-describedby={refKeyError ? 'project-ref-key-hint project-ref-key-error' : 'project-ref-key-hint'}
                        aria-invalid={refKeyError ? 'true' : undefined}
                        className="input font-mono w-40"
                      />
                      {refKeyInfo.canEdit && !refKey && refKeyInfo.suggestion && (
                        <button
                          type="button"
                          onClick={() => setRefKey(refKeyInfo.suggestion)}
                          className="btn btn-ghost btn-sm shrink-0"
                        >
                          Utiliser {refKeyInfo.suggestion}
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-slate-500" id="project-ref-key-hint">
                      Facultative. Avec une clé, les tickets du projet s’identifient en {refKey || refKeyInfo.suggestion || 'ABC'}-1, {refKey || refKeyInfo.suggestion || 'ABC'}-2… De 2 à 10 lettres ou chiffres, en commençant par une lettre. La changer ou la retirer ne casse aucun lien : les anciens identifiants restent valables.
                    </p>
                    {refKeyInfo.history?.length > 0 && (
                      <p className="mt-1 text-xs text-slate-500">
                        Anciennes clés, toujours valables : {refKeyInfo.history.join(', ')}
                      </p>
                    )}
                    {!refKeyInfo.canEdit && (
                      <p className="mt-1 text-xs text-slate-500">Seul un administrateur du projet peut changer la clé.</p>
                    )}
                    {refKeyError && <p className="mt-1 text-xs text-rose-600" id="project-ref-key-error" role="alert">{refKeyError}</p>}
                  </>
                )}
                <label className="field-label mb-2 mt-4">Description</label>
                <textarea
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                  rows={3}
                  placeholder="Contexte client, périmètre…"
                  className="textarea"
                />
                {/* Les deux listes se rendent MÊME vides : l'état du champ vient
                    des props, jamais du DOM, donc une liste non chargée affiche un
                    champ vide mais n'écrit rien (le diff ne patche qu'un vrai
                    changement). Les masquer donnait un onglet qui perdait des
                    champs sans rien dire. */}
                <label className="field-label mb-2 mt-4" htmlFor="project-account">Client</label>
                {!accountCreateMode ? (
                  <select
                    id="project-account"
                    value={projectAccount}
                    onChange={(e) => {
                      if (e.target.value === CREATE_NEW_ACCOUNT) {
                        // La sélection ne change PAS : on ouvre la saisie et le
                        // client courant reste en place tant que rien n'est créé
                        // (annuler doit rendre le projet intact).
                        setAccountError('');
                        setAccountCreateMode(true);
                        return;
                      }
                      setProjectAccount(e.target.value);
                    }}
                    className="select"
                  >
                    <option value="">— Aucun —</option>
                    {allAccountOptions.map((o) => (
                      <option key={o.value} value={String(o.value)}>{o.label}</option>
                    ))}
                    <option value={CREATE_NEW_ACCOUNT}>＋ Créer un client…</option>
                  </select>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newAccountName}
                      onChange={(e) => setNewAccountName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); handleCreateAccount(); }
                        // Échap sort de la saisie SANS fermer la pop-in : l'écoute
                        // du dialogue se fait plus haut, il faut donc l'arrêter ici.
                        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancelAccountCreate(); }
                      }}
                      placeholder="Nom du nouveau client"
                      aria-label="Nom du nouveau client"
                      disabled={accountCreating}
                      autoFocus
                      className="input flex-1"
                    />
                    <button
                      type="button"
                      onClick={handleCreateAccount}
                      disabled={!newAccountName.trim() || accountCreating}
                      className="btn btn-primary btn-sm shrink-0"
                    >
                      {accountCreating ? 'Création…' : 'Créer'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelAccountCreate}
                      disabled={accountCreating}
                      aria-label="Annuler la création du client"
                      className="btn btn-secondary btn-sm shrink-0"
                    >
                      Annuler
                    </button>
                  </div>
                )}
                {accountError && <p className="mt-1 text-xs text-rose-600">{accountError}</p>}

                <label className="field-label mb-2 mt-4" htmlFor="project-methodology">Méthodologie</label>
                {/* Option vide OBLIGATOIRE : sans elle, un projet sans
                    méthodologie afficherait la première de la liste alors que
                    la donnée reste vide — et c'est cet état vide qui laisse le
                    template de projet poser la sienne. */}
                <select
                  id="project-methodology"
                  value={projectMethodology}
                  onChange={(e) => setProjectMethodology(e.target.value)}
                  className="select"
                >
                  <option value="">— Aucune —</option>
                  {methodologyOptions.map((o) => (
                    <option key={o.value} value={String(o.value)}>{o.label}</option>
                  ))}
                </select>

                {/* Domaines — c'est ce qui rattache un signalement à ce projet :
                    le widget envoie le domaine de la page, le serveur en déduit
                    le projet (ProjectHostResolver). Multivalué, en items objets
                    `{ value }` : la forme que le back rend ET attend. */}
                <label className="field-label mb-2 mt-4" htmlFor="project-host">Noms de domaine</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    id="project-host"
                    value={newHost}
                    onChange={(e) => setNewHost(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addHost(); } }}
                    autoComplete="off"
                    inputMode="url"
                    placeholder="recette.exemple.fr"
                    aria-describedby="project-host-hint"
                    className="input flex-1"
                  />
                  <button
                    type="button"
                    onClick={addHost}
                    disabled={!newHost.trim()}
                    className="btn btn-secondary btn-sm shrink-0"
                  >
                    Ajouter
                  </button>
                </div>
                <p className="mt-1 text-xs text-slate-500" id="project-host-hint">
                  Un domaine par entrée, sans protocole ni port. Les domaines instables (localhost, tunnels) ne servent à rien ici : le formulaire de signalement laisse alors choisir le projet.
                </p>
                {projectHosts.length > 0 ? (
                  <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Domaines du projet">
                    {projectHosts.map((h, index) => {
                      const value = h?.value ?? h;
                      return (
                        <li key={`${value}-${index}`} className="badge badge-neutral font-mono inline-flex items-center gap-1">
                          <span>{value}</span>
                          <button
                            type="button"
                            onClick={() => setProjectHosts((prev) => prev.filter((_, i) => i !== index))}
                            aria-label={`Retirer ${value}`}
                            className="inline-flex h-6 w-6 items-center justify-center rounded-full hover:text-rose-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-iris-400"
                          >
                            <X size={12} aria-hidden="true" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="mt-2 text-xs text-slate-400">Aucun domaine pour le moment.</p>
                )}

                {/* Instance de l'agent IA (orbit_agent_relay) : où partent les
                    analyses et résolutions des tickets de ce projet, et avec
                    quelle clé. Sans clé, le bouton du ticket reste désactivé
                    et le dit. */}
                <label className="field-label mb-2 mt-4" htmlFor="project-agent-instance">Instance de l’agent IA</label>
                <input
                  type="url"
                  id="project-agent-instance"
                  value={projectAgentInstance}
                  onChange={(e) => setProjectAgentInstance(e.target.value)}
                  autoComplete="off"
                  inputMode="url"
                  placeholder="https://drupal.projet.exemple"
                  aria-describedby="project-agent-instance-hint"
                  className="input"
                />
                <p className="mt-1 text-xs text-slate-500" id="project-agent-instance-hint">
                  Adresse de l’instance Orbit qui exécute les analyses et résolutions IA des tickets de ce projet (http(s)://hôte, sans chemin). Vide : pas d’agent IA.
                </p>

                <label className="field-label mb-2 mt-4" htmlFor="project-agent-key">Clé d’API de l’instance</label>
                <div className="flex items-center gap-2">
                  <input
                    type="password"
                    id="project-agent-key"
                    value={agentKeyInput}
                    onChange={(e) => { setAgentKeyInput(e.target.value); setAgentKeyRemove(false); setAgentKeyError(''); }}
                    autoComplete="new-password"
                    spellCheck={false}
                    placeholder={agentKey.set ? `Enregistrée (${agentKey.prefix}) — laisser vide pour la conserver` : 'orb_…'}
                    aria-describedby="project-agent-key-hint"
                    aria-invalid={agentKeyError ? 'true' : undefined}
                    className={`input flex-1${agentKeyError ? ' input-error' : ''}`}
                  />
                  {agentKey.set && (
                    <button
                      type="button"
                      onClick={() => { setAgentKeyRemove((on) => !on); setAgentKeyInput(''); setAgentKeyError(''); }}
                      className={`btn btn-sm shrink-0 ${agentKeyRemove ? 'btn-secondary' : 'btn-ghost-danger'}`}
                    >
                      {agentKeyRemove ? 'Garder la clé' : 'Retirer'}
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-slate-500" id="project-agent-key-hint">
                  {agentKeyRemove
                    ? 'La clé sera retirée à l’enregistrement.'
                    : 'Émise sur l’instance du projet : « Mes clés API », en cochant « Relais agent IA ». Elle n’est jamais réaffichée ici.'}
                </p>
                {agentKeyError && (
                  <p role="alert" className="alert alert-danger mt-2 text-sm">{agentKeyError}</p>
                )}

                <span className="field-label mb-2 mt-4 block">Picto du projet</span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setPictoLibraryOpen(true)}
                    aria-label={`Choisir le picto du projet${projectPicto ? ` (actuellement : ${projectPicto})` : ''}`}
                    className="picto-trigger"
                  >
                    <Picto name={projectPicto} size={24} />
                  </button>
                  <button type="button" onClick={() => setPictoLibraryOpen(true)} className="btn btn-ghost btn-sm">
                    {projectPicto ? 'Changer l’icône' : 'Choisir une icône'}
                  </button>
                </div>
                {pictoLibraryOpen && (
                  <IconLibrary
                    value={isPictoName(projectPicto) ? projectPicto : null}
                    title="Bibliothèque d’icônes — picto du projet"
                    onPick={setProjectPicto}
                    onClose={() => setPictoLibraryOpen(false)}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            Annuler
          </button>
          <button
            onClick={async () => {
              // The ticket key goes FIRST: it is the save most likely to be
              // refused for a business reason (key taken, 422), and nothing
              // else should be written before the person has fixed it. The
              // focus goes back to the field, where the reason is announced.
              const refKeySaved = await saveRefKey();
              if (!refKeySaved) {
                setActiveTab('general');
                setTimeout(() => refKeyInputRef.current?.focus(), 0);
                return;
              }
              // The matrix is saved on its own endpoint; a refusal there keeps
              // the modal open so the message is visible.
              const rightsSaved = await saveRights();
              if (!rightsSaved) {
                setActiveTab('rights');
                return;
              }
              // La clé de l'instance part sur sa propre route (entité Key) ;
              // refusée, la pop-in reste ouverte sur l'onglet et le message.
              const agentKeySaved = await saveAgentKeyIfNeeded();
              if (!agentKeySaved) {
                setActiveTab('general');
                return;
              }
              // `hideEstimation` n'est plus dans cette pop-in : ne PAS l'envoyer.
              // Le diff laisse alors la valeur stockée intacte — l'envoyer à
              // `false` rouvrirait le chiffrage aux clients à chaque save.
              onSave && onSave({
                title: projectTitle,
                description: projectDescription,
                picto: projectPicto,
                methodology: projectMethodology,
                account: projectAccount,
                hosts: projectHosts,
                agentInstance: projectAgentInstance,
                enabledTaskTypes,
                workingHours,
              });
              onClose();
            }}
            className="btn btn-primary"
          >
            Enregistrer
          </button>
        </div>
      </div>

      <WorkflowViewModal
        isOpen={viewWorkflowModal.isOpen}
        onClose={() => setViewWorkflowModal({ isOpen: false, workflowId: null, workflowLabel: null })}
        workflowId={viewWorkflowModal.workflowId}
        workflowLabel={viewWorkflowModal.workflowLabel}
        canEdit={canEditWorkflow}
      />
    </div>
  );
};

export default ProjectSettingsModal;
